import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { compressAccurately } from 'image-conversion'
import PQueue from 'p-queue'

import { ColumnTypes } from '@makyoapp/makyo-sdk/models/Table'
import { ImageUploadValue } from '@makyoapp/makyo-tw-ui'

import { CoreComponentFormProps, CoreComponentInfos } from '@/core/models/app-components/types'
import { useAppBuilderContext } from '@/context/AppBuilderContext'
import { useParseVariableSelect } from '@/hooks/__project__/useParseVariableSelect'
import { useParseVariableText } from '@/hooks/__project__/useParseVariableText'
import { ComponentSettingKey, MdImageUploadValue } from '@/shared/builder/types'
import { getSafely } from '@/shared/safely'
import { isEmpty, isFile } from '@/shared/utils'
import { CompOverlayDevmode } from '@/components/__project__/shared/CompOverlayDevmode'

import { getFileSizeInBytes, validateFileTypes } from '../../../form-utils'
import { useUpdateFormDefaultValue } from '../../../hooks/useUpdateFormDefaultValue'
import { CompImageUploadProps } from './comp'

const pqueue = new PQueue({ concurrency: 1 })

function getDimensionFromDataUrl(dataUrl: string) {
  return new Promise<{ width: number; height: number }>((resolve) => {
    const img = new Image()
    img.onload = () => {
      resolve({ width: img.width, height: img.height })
    }
    img.src = dataUrl
  })
}

async function compressImage(file: MdImageUploadValue, compressionMaxSize: number): Promise<MdImageUploadValue> {
  if (!file || !isFile(file)) return file
  try {
    const originalFile = new File([file], file.name, { type: file.type })

    // get the width and height of the image
    const { width, height } = await getDimensionFromDataUrl(URL.createObjectURL(originalFile))

    // started from 4k images (3840 x 2160), we will scale the dimension
    const isLargeDimension = width > 2160 || height > 2160

    const compressedFile = await compressAccurately(originalFile, {
      size: compressionMaxSize,
      accuracy: 0.9,
      scale: isLargeDimension ? 0.5 : 1,
    })
    return new File([compressedFile], originalFile.name, { type: originalFile.type })
    //
  } catch (error) {
    return file
  }
}

export interface CompImageUploadWrapperProps {
  infos: CoreComponentInfos
  form: CoreComponentFormProps
  children: (props: CompImageUploadProps) => React.ReactNode
}

/**
 * CompImageUploadWrapperCore
 */
export const CompImageUploadWrapperCore = ({
  infos: { settings, containerClient, detailTableId, detailRowIndex, component },
  form,
  children,
}: CompImageUploadWrapperProps) => {
  const { openComponentSettings, isDevelopmentMode } = useAppBuilderContext()

  const handleClick = () => {
    openComponentSettings(component)
    if (isDevelopmentMode) return
  }

  // ========================= form settings =========================
  const { dataSourceTable, formState, setFormState, formErrors, setFormErrors, setRequiredFieldStatus } = form

  // ========================= data =========================
  const columnId = settings?.[ComponentSettingKey.ImageUploadDataValue] as string
  const column = dataSourceTable?.columnsMap.get(columnId)
  const isMultiple = column?._Type === ColumnTypes.ImageArray

  const defaultValue = useParseVariableSelect(
    settings?.[ComponentSettingKey.ImageUploadDataDefaultValue],
    containerClient,
    detailTableId,
    detailRowIndex
  )

  const label = useParseVariableText(
    settings?.[ComponentSettingKey.ImageUploadDataLabel],
    containerClient,
    detailTableId,
    detailRowIndex
  )

  const helpText = useParseVariableText(
    settings?.[ComponentSettingKey.ImageUploadDataHelp],
    containerClient,
    detailTableId,
    detailRowIndex
  )

  // ========================= design =========================

  // ========================= behavior =========================
  const maxSize = getSafely(settings, [ComponentSettingKey.ImageUploadBehaviorMaxSize], {
    value: 10,
    unit: 'mb',
  })
  const maxSizeValue = getFileSizeInBytes(maxSize.value, maxSize.unit)

  const compressionMaxSize = getSafely(settings, [ComponentSettingKey.ImageUploadBehaviorCompressionMaxSize], [150])[0] // 150kb default

  const acceptedFileTypes = getSafely(
    settings,
    [ComponentSettingKey.ImageUploadBehaviorAllowedFileTypes],
    [] as string[]
  )

  const maxItems = getSafely(settings, [ComponentSettingKey.ImageUploadBehaviorMaxItems], 5)

  const required = getSafely(settings, [ComponentSettingKey.ImageUploadBehaviorRequired], false)
  const showHelpText = getSafely(settings, [ComponentSettingKey.ImageUploadBehaviorShowHelpText], false)

  const defaultHelpText = isMultiple
    ? `Up to ${maxItems} images (${acceptedFileTypes.join(', ')}), max ${maxSize.value} ${maxSize.unit} each`
    : `File type: ${acceptedFileTypes.join(', ')}, max ${maxSize.value} ${maxSize.unit}`

  // ========================= form state =========================
  const value = useMemo(() => {
    const currentValue = formState[columnId] as ImageUploadValue[] | undefined

    if (typeof currentValue === 'undefined') {
      return undefined
    }

    if (currentValue === null) {
      return []
    }

    // For multiple images (ImageArray type)
    if (isMultiple) {
      return Array.isArray(currentValue) ? currentValue : []
    }
    // For single image, ensure we always return an array
    return Array.isArray(currentValue) ? currentValue.slice(0, 1) : [currentValue]
  }, [formState, columnId, isMultiple])

  const handleUpdateDefaultValue = useCallback(
    (defValue: MdImageUploadValue[] | null) => {
      if (!defValue) {
        setFormState((prev) => ({ ...prev, [columnId]: [] }))
      } else {
        setFormState((prev) => ({ ...prev, [columnId]: isMultiple ? [defValue] : defValue }))
      }
    },
    [columnId, setFormState, isMultiple]
  )

  useUpdateFormDefaultValue({
    defaultValue,
    value,
    onUpdate: handleUpdateDefaultValue,
  })

  const error = formErrors?.[columnId]

  const [isCompressing, setIsCompressing] = useState(false)
  const [debouncedValue, setDebouncedValue] = useState<MdImageUploadValue[]>(value ?? [])
  const debounceTimeout = useRef<NodeJS.Timeout>()

  const handleChange = async (files: MdImageUploadValue[]) => {
    if (!columnId) return

    setIsCompressing(true)

    try {
      let selectedFiles = files

      // Prevent exceeding maxItems
      if (files.length > maxItems) {
        // take the first maxItems files
        selectedFiles = files.slice(0, maxItems)
      }

      const compressedFiles = (await Promise.all(
        selectedFiles.map((file) => pqueue.add(async () => compressImage(file, compressionMaxSize)))
      )) as MdImageUploadValue[]

      // Clear previous errors
      setFormErrors((prev) => {
        const newErrors = { ...prev }
        delete newErrors[columnId]
        return newErrors
      })

      // Update form state with the correct type based on isMultiple
      setFormState((prev) => ({
        ...prev,
        [columnId]: isMultiple ? compressedFiles : compressedFiles.length > 0 ? compressedFiles[0] : null,
      }))

      // Set debounced value for validation
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current)
      }
      debounceTimeout.current = setTimeout(() => {
        setDebouncedValue(compressedFiles)
      }, 500)
    } finally {
      setIsCompressing(false)
    }
  }

  const handleClear = () => {
    if (!columnId) return

    setFormState((prev) => ({ ...prev, [columnId]: null }))
    setDebouncedValue([])
    setFormErrors((prev) => {
      const newErrors = { ...prev }
      delete newErrors[columnId]
      return newErrors
    })
  }

  const validateField = useCallback(
    (files: MdImageUploadValue[]) => {
      if (files && files.length > 0) {
        // Check file sizes
        const oversizedFile = files.find((f) => isFile(f) && f.size > maxSizeValue)
        if (oversizedFile) {
          setFormErrors((prev) => ({ ...prev, [columnId]: 'File size exceeds the maximum allowed size' }))
          return
        }

        // Check file types
        const invalidFileType = files.find(
          (f) => isFile(f) && acceptedFileTypes.length > 0 && !validateFileTypes(f, acceptedFileTypes)
        )
        if (invalidFileType) {
          setFormErrors((prev) => ({ ...prev, [columnId]: 'Invalid file type' }))
          return
        }
      }

      setFormErrors((prev) => {
        const newErrors = { ...prev }
        delete newErrors[columnId]
        return newErrors
      })
    },
    [columnId, maxSizeValue, acceptedFileTypes, setFormErrors]
  )

  // Use effect for validation
  useEffect(() => {
    validateField(debouncedValue)
  }, [debouncedValue, validateField])

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current)
        debounceTimeout.current = undefined
      }
      if (pqueue.size > 0) {
        pqueue.clear()
      }
    }
  }, [])

  // Add effect to update required field status
  useEffect(() => {
    if (required) {
      setRequiredFieldStatus(columnId, isEmpty(value))
    }
    return () => {
      if (required) {
        setRequiredFieldStatus(columnId, false)
      }
    }
  }, [required, value, columnId, setRequiredFieldStatus])

  // ========================= form state =========================

  const imageUploadProps: CompImageUploadProps = {
    required,
    label,
    error,
    accept: acceptedFileTypes.length > 0 ? acceptedFileTypes.join(',') : '*',
    multiple: isMultiple,
    value: value ?? [],
    onChange: handleChange,
    onClear: handleClear,
    supportingText: showHelpText ? helpText || defaultHelpText : undefined,
    isLoading: isCompressing,
  }

  if (isDevelopmentMode) {
    return (
      <div className='relative inline-block w-full border border-dashed' style={{ borderColor: '#333' }}>
        {children(imageUploadProps)}
        <CompOverlayDevmode handleClick={handleClick} />
      </div>
    )
  }

  return children(imageUploadProps)
}
